import { useMemo, useState } from 'react'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { PencilIcon } from '../../components/icons/PencilIcon'
import { TemplateIcon } from '../../components/icons/TemplateIcon'
import { WarningIcon } from '../../components/icons/WarningIcon'
import { splitOpsForTree } from './opsTreeData'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch, ApiError } from '../../lib/api'
import type {
  PermissionTemplate,
  PermissionTemplateOperation,
  PermissionTemplateCreate,
  MambuConnectionOperation,
  UserAccess,
} from '../../lib/types'
import { OperationsTree } from '../../components/OperationsTree'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useEntityEditorRegistration } from '../../hooks/useEntityEditorRegistration'
import { Spinner } from '../../components/Spinner'
import { Card } from '../../components/Card'

/* ─── Icons ─── */

/* ─── Props ─── */

export interface TemplatesTabProps {
  connectionId: string
  templates: PermissionTemplate[]
  tenantOperations: MambuConnectionOperation[]
  userAccess: UserAccess[]
  defaultPermissionTemplateId: string | null
}

type EditorMode = 'idle' | 'create' | 'edit'

interface DeleteDependent {
  principalSub?: string
  principalDisplayName?: string
  isDefaultAccess?: boolean
}

export function TemplatesTab({ connectionId, templates, tenantOperations, userAccess, defaultPermissionTemplateId }: TemplatesTabProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()
  const { treeOps, deprecatedTreeOps } = useMemo(
    () => splitOpsForTree(tenantOperations),
    [tenantOperations],
  )

  const [editorMode, setEditorMode] = useState<EditorMode>('idle')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [editorName, setEditorName] = useState('')
  const [editorOps, setEditorOps] = useState<PermissionTemplateOperation[]>([])
  const [editorError, setEditorError] = useState<string | null>(null)

  const [deleteConfirm, setDeleteConfirm] = useState<PermissionTemplate | null>(null)
  const [deleteDependents, setDeleteDependents] = useState<DeleteDependent[] | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [editWarnConfirm, setEditWarnConfirm] = useState(false)

  /* ── Entity-scope unsaved-changes protection (spec §7a) ──
     The template editor commits its own entity and never dirties the page, so without a
     baseline of its own it would be unguarded: typing a name and clicking another template
     would discard the edit silently. `editorBaseline` is the fingerprint taken when the
     editor opened; `pendingSelect` holds the entity the admin tried to open while dirty. */
  const [editorBaseline, setEditorBaseline] = useState<string | null>(null)
  const [pendingSelect, setPendingSelect] = useState<PermissionTemplate | 'create' | null>(null)

  const templatesKey = ['mambu-connections', connectionId, 'templates'] as const

  function editorFingerprint(name: string, operations: PermissionTemplateOperation[]): string {
    const enabled = operations.filter(o => o.isEnabled).map(o => o.operationId).sort()
    return JSON.stringify({ name, enabled })
  }

  const isEditorDirty = editorBaseline !== null
    && editorFingerprint(editorName, editorOps) !== editorBaseline

  function resetEditor() {
    setEditorMode('idle'); setSelectedTemplateId(null)
    setEditorName(''); setEditorOps([]); setEditorError(null)
    setEditorBaseline(null); setPendingSelect(null)
  }

  useEntityEditorRegistration({
    isOpen: () => editorMode !== 'idle',
    isDirty: () => isEditorDirty,
    markClean: () => resetEditor(),
  })

  const createMutation = useMutation({
    mutationFn: (data: PermissionTemplateCreate) =>
      apiFetch<PermissionTemplate>(
        `/api/v1/mambu-connections/${connectionId}/permission-templates`,
        { method: 'POST', body: JSON.stringify(data) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey })
      resetEditor()
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('mambuMcpDetail.errors.failedToSave'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PermissionTemplateCreate }) =>
      apiFetch<PermissionTemplate>(
        `/api/v1/mambu-connections/${connectionId}/permission-templates/${id}`,
        { method: 'PUT', body: JSON.stringify(data) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey })
      resetEditor()
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('mambuMcpDetail.errors.failedToSave'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(
        `/api/v1/mambu-connections/${connectionId}/permission-templates/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: templatesKey })
      if (selectedTemplateId === id) resetEditor()
      setDeleteConfirm(null); setDeleteDependents(null); setDeleteError(null)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409 && err.dependents?.length) {
        setDeleteDependents(err.dependents as DeleteDependent[])
      } else {
        setDeleteError(err instanceof Error ? err.message : t('mambuMcpDetail.errors.failedToSave'))
      }
    },
  })

  function handleAddNew() {
    setEditorMode('create'); setSelectedTemplateId(null)
    setEditorName(''); setEditorOps([]); setEditorError(null)
    setEditorBaseline(editorFingerprint('', []))
  }

  function handleSelect(template: PermissionTemplate) {
    setEditorMode('edit'); setSelectedTemplateId(template.id)
    setEditorName(template.name)
    setEditorOps(template.operations.map(o => ({ ...o })))
    setEditorError(null)
    setEditorBaseline(editorFingerprint(template.name, template.operations))
  }

  /* Opening ANY template while the current one has unsaved edits must ask first (§7a) —
     the same generic dialog the page and navigation guards use. Deliberately not
     conditional on the clicked template differing from the open one: `handleSelect`
     rebuilds the form from the stored template either way, so re-opening the template
     already being edited discards the edits just as surely as switching away. The
     prototype guards the same way (agent-studio.html:11574). */
  function requestSelect(template: PermissionTemplate) {
    if (isEditorDirty) {
      setPendingSelect(template)
      return
    }
    handleSelect(template)
  }

  function requestAddNew() {
    if (isEditorDirty) {
      setPendingSelect('create')
      return
    }
    handleAddNew()
  }

  function commitSave() {
    const name = editorName.trim()
    if (!name) return
    const data: PermissionTemplateCreate = { name, operations: editorOps }
    if (editorMode === 'edit' && selectedTemplateId) {
      updateMutation.mutate({ id: selectedTemplateId, data })
    } else {
      createMutation.mutate(data)
    }
    setEditWarnConfirm(false)
  }

  function handleSaveClick() {
    if (!editorName.trim()) return
    if (editorMode === 'edit' && selectedTemplateId) {
      const affectedUsers = userAccess.filter(u => u.templateId === selectedTemplateId).length
      const isDefault = selectedTemplateId === defaultPermissionTemplateId
      if (affectedUsers > 0 || isDefault) {
        setEditWarnConfirm(true)
        return
      }
    }
    commitSave()
  }

  function openDelete(template: PermissionTemplate) {
    setDeleteConfirm(template); setDeleteDependents(null); setDeleteError(null)
  }

  function dismissDelete() {
    setDeleteConfirm(null); setDeleteDependents(null); setDeleteError(null)
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const canSave = editorName.trim().length > 0 && !isSaving
  const isBlocked = deleteDependents !== null && deleteDependents.length > 0
  const editAffectedUsers = selectedTemplateId
    ? userAccess.filter(u => u.templateId === selectedTemplateId).length
    : 0
  const editAffectsDefault = selectedTemplateId !== null && selectedTemplateId === defaultPermissionTemplateId

  return (
    <Card title={t('mambuMcpDetail.templates.cardTitle')} subtitle={t('mambuMcpDetail.templates.cardSubtitle')} noPadding className="editor-split-card">
      <div className="editor-split-body">

        {/* ── Left: template list ── */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {templates.map(template => {
            const enabledCount = template.operations.filter(o => o.isEnabled).length
            const usedByCount = userAccess.filter(u => u.templateId === template.id).length
            const isDefault = template.id === defaultPermissionTemplateId
            const deleteDisabled = usedByCount > 0 || isDefault
            const metaParts = [t('mambuMcpDetail.templates.opCount', { count: enabledCount })]
            if (usedByCount > 0) metaParts.push(t('mambuMcpDetail.templates.usedBy', { count: usedByCount }))
            if (isDefault) metaParts.push(t('mambuMcpDetail.templates.defaultTag'))
            return (
              <div
                key={template.id}
                /* The prototype never adds `active` to a template row (:11603) — selection is
                   conveyed solely by the editor opening in the right pane. */
                className="skill-row tpl-item"
                onClick={() => requestSelect(template)}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-subtle)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <TemplateIcon size={16} />
                </div>
                <div className="skill-info">
                  <div className="skill-name">{template.name}</div>
                  <div className="skill-desc">{metaParts.join(' · ')}</div>
                </div>
                <button
                  type="button"
                  aria-label={t('mambuMcpDetail.templates.editAria')}
                  onClick={e => { e.stopPropagation(); requestSelect(template) }}
                  className="btn-text skill-edit-btn"
                  title={t('mambuMcpDetail.templates.editAria')}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  aria-label={t('mambuMcpDetail.templates.deleteAria')}
                  onClick={e => { e.stopPropagation(); openDelete(template) }}
                  className="btn-text skill-remove-btn"
                  title={deleteDisabled ? t('mambuMcpDetail.templates.deleteDisabledTitle') : t('mambuMcpDetail.templates.deleteAria')}
                  disabled={deleteDisabled}
                  style={deleteDisabled ? { opacity: 0.35, cursor: 'not-allowed' } : {}}
                >
                  <TrashIcon />
                </button>
              </div>
            )
          })}
        </div>

        {/* separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* ── Right: editor panel ── */}
        <div className="editor-pane-col">

          {editorMode === 'idle' && (
            <div className="editor-pane-empty">
              <TemplateIcon />
              <p style={{ fontSize: 13 }}>{t('mambuMcpDetail.templates.emptyStateTitle')}</p>
              <button className="btn btn-primary" onClick={requestAddNew}>{t('mambuMcpDetail.templates.addNew')}</button>
            </div>
          )}

          {(editorMode === 'create' || editorMode === 'edit') && (
            <div className="editor-form-wrap">
              <div className="editor-form-scroll">
                {editorError && (
                  <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{editorError}</div>
                )}

                <div className="form-group">
                  <label htmlFor="template-name" className="form-label">
                    {t('mambuMcpDetail.templates.nameLabel')}
                    <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    id="template-name"
                    type="text"
                    value={editorName}
                    onChange={e => setEditorName(e.target.value)}
                    placeholder={t('mambuMcpDetail.templates.namePlaceholder')}
                    className="form-input"
                    style={{ maxWidth: '100%' }}
                    required
                  />
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
                  {t('mambuMcpDetail.templates.operationsLabel')}
                </div>

                <OperationsTree
                  ops={treeOps}
                  deprecatedOps={deprecatedTreeOps}
                  deprecatedGroupLabel={<><WarningIcon />{t('mambuMcpDetail.opsTree.deprecatedGroup')}</>}
                  mode="template"
                  isChecked={(id) => editorOps.some(o => o.operationId === id && o.isEnabled)}
                  onToggle={(id, checked) => setEditorOps(prev => {
                    const exists = prev.some(o => o.operationId === id)
                    return exists
                      ? prev.map(o => o.operationId === id ? { ...o, isEnabled: checked } : o)
                      : [...prev, { operationId: id, isEnabled: checked }]
                  })}
                  isTenantEnabled={(id) => tenantOperations.find(o => o.id === id)?.isEnabled ?? false}
                  ariaLabel={t('mambuMcpDetail.opsTree.treeAriaLabel', { name: editorName || t('mambuMcpDetail.templates.cardTitle') })}
                />
              </div>

              <div className="editor-form-bar" style={{ gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={resetEditor} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>
                  {t('mambuMcpDetail.templates.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveClick}
                  disabled={!canSave}
                  className="btn btn-primary"
                  style={{ fontSize: 12, padding: '5px 12px', opacity: canSave ? 1 : 0.6, cursor: canSave ? 'pointer' : 'default' }}
                >
                  {isSaving ? <Spinner size={12} /> : null}
                  {t('mambuMcpDetail.templates.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete dialog — normal + blocked (in-use) modes */}
      {deleteConfirm && (
        isBlocked ? (
          <ConfirmDialog
            title={t('mambuMcpDetail.templates.deleteDialog.inUseTitle')}
            message={t('mambuMcpDetail.templates.deleteDialog.inUseMessage')}
            body={
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                  {t('mambuMcpDetail.templates.deleteDialog.dependentsLabel')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {deleteDependents!.map((d, i) => (
                    <div key={d.principalSub ?? `default-${i}`} style={{
                      padding: '7px 12px', borderRadius: 6, background: 'var(--bg-badge)',
                      fontSize: 13, color: 'var(--text-primary)',
                    }}>
                      {d.isDefaultAccess
                        ? t('mambuMcpDetail.templates.deleteDialog.defaultAccessDependent')
                        : (d.principalDisplayName || d.principalSub)}
                    </div>
                  ))}
                </div>
              </div>
            }
            confirmLabel={t('mambuMcpDetail.templates.deleteDialog.cancel')}
            cancelLabel={t('mambuMcpDetail.templates.deleteDialog.cancel')}
            onConfirm={dismissDelete}
            onCancel={dismissDelete}
          />
        ) : (
          <ConfirmDialog
            title={t('mambuMcpDetail.templates.deleteDialog.title', { name: deleteConfirm.name })}
            message={t('mambuMcpDetail.templates.deleteDialog.message')}
            body={deleteError ? (
              <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{deleteError}</div>
            ) : undefined}
            confirmLabel={t('mambuMcpDetail.templates.deleteDialog.confirm')}
            cancelLabel={t('mambuMcpDetail.templates.deleteDialog.cancel')}
            danger
            onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
            onCancel={dismissDelete}
          />
        )
      )}

      {/* Entity-scope unsaved-changes guard — raised when the admin opens a different
          template (or starts a new one) while the open editor has unsaved edits (§7a). */}
      {pendingSelect && (
        <UnsavedChangesDialog
          onConfirm={() => {
            const next = pendingSelect
            setPendingSelect(null)
            if (next === 'create') handleAddNew()
            else handleSelect(next)
          }}
          onCancel={() => setPendingSelect(null)}
        />
      )}

      {/* Editing-a-template-in-use warning */}
      {editWarnConfirm && (
        <ConfirmDialog
          title={t('mambuMcpDetail.templates.editWarnDialog.title', { name: editorName })}
          message={t('mambuMcpDetail.templates.editWarnDialog.message')}
          body={
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)' }}>
              {editAffectedUsers > 0 && (
                <li>{t('mambuMcpDetail.templates.editWarnDialog.affectedUsers', { count: editAffectedUsers })}</li>
              )}
              {editAffectsDefault && (
                <li>{t('mambuMcpDetail.templates.editWarnDialog.affectedDefault')}</li>
              )}
            </ul>
          }
          confirmLabel={t('mambuMcpDetail.templates.editWarnDialog.confirm')}
          cancelLabel={t('mambuMcpDetail.templates.editWarnDialog.cancel')}
          onConfirm={commitSave}
          onCancel={() => setEditWarnConfirm(false)}
        />
      )}
    </Card>
  )
}
